import React, { useState, useEffect } from 'react';
import {
  Box, CssBaseline, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Typography, Menu, MenuItem,
  Chip, Collapse, Tooltip, Divider, Avatar,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, ExpandLess, ExpandMore,
  Dashboard as DashboardIcon, ShoppingCart as OrdersIcon,
  People as CustomersIcon, Inventory as ProductsIcon,
  AccountCircle as AccountIcon, Security as SecurityIcon,
  AdminPanelSettings as AdminIcon, CloudUpload as UploadIcon,
  Assessment as QualityIcon, Api as ApiIcon, Storage as StorageIcon,
  Add as AddIcon, History as HistoryIcon, AccountTree as WorkflowIcon,
  Timeline as LineageIcon, QueryStats as Schema, Widgets as WidgetsIcon,
  Build as BuildIcon, Psychology as NeuronIcon, TrendingUp as SalesIcon,
  SmartToy as AgentIcon, Factory as FactoryIcon, Polyline as DiagramIcon,
  Description as FileIcon, Receipt as SalesOrderIcon,
  PrecisionManufacturing as WorkOrderIcon, LocalShipping as PurchaseOrderIcon,
  Schedule as SchedulingIcon, Work as ShopFloorIcon,
  VerifiedUser as QualityNavIcon, Hub as SupplyChainIcon,
  Category as ProductsNavIcon, Science as MaterialsIcon,
  Calculate as PlanningIcon, AutoAwesome as BOMStudioIcon,
  SupportAgent as RolesIcon, ManageAccounts as PMIcon,
  PointOfSale as SMIcon, Gavel as PurchasingMgrIcon,
  PowerSettingsNew as LogoutIcon, Bolt as NeuronBoltIcon,
  Construction as ConstructionIcon, BarChart as AnalyticsIcon,
  HealthAndSafety as MaintenanceIcon, DataObject as DataFusionIcon,
  Science as MLIcon, AutoGraph as RevenueIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';

// ─── Dimensions ───────────────────────────────────────────────────────────────
const W_OPEN = 248;
const W_CLOSED = 60;

// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  bg: '#06090F',
  surface: '#0C1220',
  border: '#162032',
  text: '#E2E8F0',
  muted: '#64748B',
  dim: '#334155',
  hover: '#111827',
  // Vertical accent colors
  mrp: '#38BDF8',
  neuron: '#E879F9',
  fusion: '#4ADE80',
  revenue: '#FCD34D',
  construction: '#FB923C',
  tools: '#94A3B8',
  admin: '#64748B',
  wip: '#2D3748',
};

// ─── Navigation structure ──────────────────────────────────────────────────────
interface NavItem { label: string; path: string; icon: React.ReactNode; }
interface NavGroup {
  id: string; label: string; icon: React.ReactNode;
  accent: string; items: NavItem[]; defaultOpen?: boolean;
}

// MRP sub-groups (nested inside the MRP section)
const MRP_GROUPS: NavGroup[] = [
  { id: 'roles', label: 'Roles', accent: '#E879F9', icon: <RolesIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Production Manager', path: '/mrp/roles/production', icon: <PMIcon sx={{ fontSize: 13 }} /> },
    { label: 'Sales Manager', path: '/mrp/roles/sales', icon: <SMIcon sx={{ fontSize: 13 }} /> },
    { label: 'Purchasing Manager', path: '/mrp/roles/purchasing', icon: <PurchasingMgrIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'orders', label: 'Orders', accent: C.mrp, icon: <SalesOrderIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Sales Orders', path: '/mrp/orders/sales', icon: <SalesOrderIcon sx={{ fontSize: 13 }} /> },
    { label: 'Work Orders', path: '/mrp/orders/production', icon: <WorkOrderIcon sx={{ fontSize: 13 }} /> },
    { label: 'Purchase Orders', path: '/mrp/orders/purchasing', icon: <PurchaseOrderIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'production', label: 'Production', accent: '#4ADE80', icon: <FactoryIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Scheduling', path: '/mrp/production/scheduling', icon: <SchedulingIcon sx={{ fontSize: 13 }} /> },
    { label: 'Shop Floor', path: '/mrp/production/shop-floor', icon: <ShopFloorIcon sx={{ fontSize: 13 }} /> },
    { label: 'Control Panel', path: '/mrp/production/control', icon: <FactoryIcon sx={{ fontSize: 13 }} /> },
    { label: 'BOM Management', path: '/mrp/production/boms', icon: <WorkflowIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'planning', label: 'Planning', accent: '#A78BFA', icon: <PlanningIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Demand Planning', path: '/mrp/planning/demand', icon: <SalesIcon sx={{ fontSize: 13 }} /> },
    { label: 'Purchase Planning', path: '/mrp/planning/purchasing', icon: <PlanningIcon sx={{ fontSize: 13 }} /> },
    { label: 'BOM Studio', path: '/mrp/planning/bom-studio', icon: <BOMStudioIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'inv-quality', label: 'Inventory & Quality', accent: '#FB923C', icon: <ProductsIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Inventory', path: '/mrp/inventory', icon: <ProductsIcon sx={{ fontSize: 13 }} /> },
    { label: 'Quality', path: '/mrp/quality', icon: <QualityNavIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'supply-chain', label: 'Supply Network', accent: '#F87171', icon: <SupplyChainIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Risk Dashboard', path: '/mrp/supply-chain', icon: <SupplyChainIcon sx={{ fontSize: 13 }} /> },
    { label: 'Network View', path: '/mrp/supply-chain/network', icon: <SupplyChainIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'assets', label: 'Assets', accent: '#FCD34D', icon: <WorkOrderIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Machines & Assets', path: '/mrp/assets', icon: <WorkOrderIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'partners', label: 'Partners', accent: '#34D399', icon: <CustomersIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Customers', path: '/mrp/partners/customers', icon: <CustomersIcon sx={{ fontSize: 13 }} /> },
    { label: 'Suppliers', path: '/mrp/partners/suppliers', icon: <PurchaseOrderIcon sx={{ fontSize: 13 }} /> },
  ]},
  { id: 'engineering', label: 'Engineering', accent: '#94A3B8', icon: <ProductsNavIcon sx={{ fontSize: 14 }} />, items: [
    { label: 'Product Specs', path: '/mrp/engineering/product-specs', icon: <ProductsNavIcon sx={{ fontSize: 13 }} /> },
    { label: 'Material Specs', path: '/mrp/engineering/material-specs', icon: <MaterialsIcon sx={{ fontSize: 13 }} /> },
  ]},
];

// Top-level verticals
interface TopGroup {
  id: string; label: string; subtitle: string;
  accent: string; icon: React.ReactNode; isMrp?: boolean;
  items?: NavItem[];
}
const TOP_GROUPS: TopGroup[] = [
  {
    id: 'mrp', label: 'Myelōs MRP', subtitle: 'Manufacturing Platform',
    accent: C.mrp, icon: <FactoryIcon sx={{ fontSize: 18 }} />, isMrp: true,
  },
  {
    id: 'neuron', label: 'Neuron Studio', subtitle: 'AI Developer Platform',
    accent: C.neuron, icon: <NeuronIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Studio Hub', path: '/neuron-studio', icon: <BOMStudioIcon sx={{ fontSize: 14 }} /> },
      { label: 'ML Lab', path: '/ml-studio', icon: <MLIcon sx={{ fontSize: 14 }} /> },
      { label: 'Agents', path: '/agentic-ai', icon: <AgentIcon sx={{ fontSize: 14 }} /> },
      { label: 'Intelligence', path: '/neuron-intelligence', icon: <NeuronIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'fusion', label: 'Data Fusion', subtitle: 'Data Engineering',
    accent: C.fusion, icon: <DataFusionIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Data Fusion Hub',    path: '/data-fusion',              icon: <DataFusionIcon sx={{ fontSize: 14 }} /> },
      { label: 'Axon Canvas',        path: '/data-fusion/axon',         icon: <WorkflowIcon sx={{ fontSize: 14 }} /> },
      { label: 'Connectors',         path: '/data-fusion/connectors',   icon: <ApiIcon sx={{ fontSize: 14 }} /> },
      { label: 'Spec Parser',        path: '/data-fusion/spec-parser',  icon: <FileIcon sx={{ fontSize: 14 }} /> },
      { label: 'Data Explorer',      path: '/data-fusion/explorer',     icon: <WidgetsIcon sx={{ fontSize: 14 }} /> },
      { label: 'Lineage & Quality',  path: '/data-fusion/lineage',      icon: <LineageIcon sx={{ fontSize: 14 }} /> },
      { label: 'Delta & .neuron',    path: '/data-fusion/storage',      icon: <StorageIcon sx={{ fontSize: 14 }} /> },
      { label: 'Semantic Modeling',  path: '/data-fusion/semantic',     icon: <Schema sx={{ fontSize: 14 }} /> },
      { label: 'Tensor Engine',      path: '/data-fusion/tensor',       icon: <NeuronBoltIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'revenue', label: 'Sales & Revenue', subtitle: 'Revenue Intelligence',
    accent: C.revenue, icon: <RevenueIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Revenue Hub',        path: '/sales-revenue',                 icon: <RevenueIcon sx={{ fontSize: 14 }} /> },
      { label: 'Revenue Dashboard',  path: '/sales-revenue/dashboard',        icon: <AnalyticsIcon sx={{ fontSize: 14 }} /> },
      { label: 'Customer 360',       path: '/sales-revenue/customers',        icon: <CustomersIcon sx={{ fontSize: 14 }} /> },
      { label: 'Profit Cube',        path: '/sales-revenue/profit-cube',      icon: <Schema sx={{ fontSize: 14 }} /> },
      { label: 'What-If Simulator',  path: '/sales-revenue/what-if',          icon: <PlanningIcon sx={{ fontSize: 14 }} /> },
      { label: 'AI Forecasting',     path: '/sales-revenue/forecast',         icon: <SalesIcon sx={{ fontSize: 14 }} /> },
      { label: 'Market Signal',      path: '/sales-revenue/market',           icon: <ConstructionIcon sx={{ fontSize: 14 }} /> },
      { label: 'Dual Architecture',  path: '/sales-revenue/architecture',     icon: <StorageIcon sx={{ fontSize: 14 }} /> },
      { label: 'Data Sheets',        path: '/data-sheets',                    icon: <FileIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'construction', label: 'Construction Market', subtitle: 'Market Intelligence',
    accent: C.construction, icon: <ConstructionIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Market Intelligence', path: '/sales-intelligence', icon: <AnalyticsIcon sx={{ fontSize: 14 }} /> },
      { label: 'Spec Extraction', path: '/spec-extraction', icon: <FileIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'tools', label: 'Tools', subtitle: 'Platform Utilities',
    accent: C.tools, icon: <BuildIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Diagrams', path: '/diagrams', icon: <DiagramIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'admin', label: 'Administration', subtitle: 'Platform Management',
    accent: C.admin, icon: <AdminIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'User Management', path: '/admin/users', icon: <CustomersIcon sx={{ fontSize: 14 }} /> },
      { label: 'Audit Logs', path: '/audit', icon: <SecurityIcon sx={{ fontSize: 14 }} /> },
    ],
  },
  {
    id: 'wip', label: 'WIP', subtitle: 'Work in Progress',
    accent: C.wip, icon: <HistoryIcon sx={{ fontSize: 18 }} />,
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon sx={{ fontSize: 14 }} /> },
      { label: 'Orders', path: '/orders', icon: <OrdersIcon sx={{ fontSize: 14 }} /> },
      { label: 'Customers', path: '/customers', icon: <CustomersIcon sx={{ fontSize: 14 }} /> },
      { label: 'Products', path: '/products', icon: <ProductsIcon sx={{ fontSize: 14 }} /> },
      { label: 'Data Management', path: '/data-management', icon: <UploadIcon sx={{ fontSize: 14 }} /> },
      { label: 'Data Quality', path: '/data-quality', icon: <QualityIcon sx={{ fontSize: 14 }} /> },
      { label: 'ETL Pipelines', path: '/etl', icon: <WorkflowIcon sx={{ fontSize: 14 }} /> },
    ],
  },
];

// Role visibility rules
const ROLE_RULES: Record<string, string[]> = {
  neuron: ['admin', 'data_engineer'],
  fusion: ['admin', 'data_engineer', 'analyst'],
  revenue: ['admin', 'data_engineer', 'analyst', 'demo'],
  construction: ['admin', 'data_engineer', 'analyst', 'demo'],
  tools: ['admin', 'data_engineer', 'analyst', 'demo'],
  admin: ['admin'],
  wip: ['admin', 'data_engineer'],
};

// ─── Component ────────────────────────────────────────────────────────────────
interface LayoutProps { children: React.ReactNode; }

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Which top-level verticals are expanded
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ wip: false });
  // Whether the MRP section tree is expanded
  const [mrpOpen, setMrpOpen] = useState(false);
  // Which MRP sub-groups are expanded
  const [mrpSubOpen, setMrpSubOpen] = useState<Record<string, boolean>>(
    Object.fromEntries(MRP_GROUPS.map(g => [g.id, true]))
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const onMrp = location.pathname.startsWith('/mrp');
  // Pages that manage their own padding and dark background
  const onDarkPage = onMrp
    || location.pathname === '/'
    || location.pathname.startsWith('/ml-studio')
    || location.pathname.startsWith('/agentic-ai')
    || location.pathname.startsWith('/neuron-studio')
    || location.pathname.startsWith('/neuron-intelligence')
    || location.pathname.startsWith('/semantic-modeling')
    || location.pathname.startsWith('/predictive-maintenance')
    || location.pathname.startsWith('/data-fusion')
    || location.pathname.startsWith('/sales-revenue');
  const path = location.pathname;

  // Auto-open vertical when navigating into it
  useEffect(() => {
    if (onMrp) {
      setMrpOpen(true);
      MRP_GROUPS.forEach(g => {
        if (g.items.some(i => path.startsWith(i.path))) {
          setMrpSubOpen(p => ({ ...p, [g.id]: true }));
        }
      });
    } else {
      TOP_GROUPS.forEach(g => {
        if (!g.isMrp && g.items?.some(i => path === i.path || path.startsWith(i.path + '/'))) {
          setOpenGroups(p => ({ ...p, [g.id]: true }));
        }
      });
    }
  }, [path]);

  const canSee = (groupId: string) => {
    if (!ROLE_RULES[groupId]) return true;
    return ROLE_RULES[groupId].includes(user?.role || '');
  };

  const isItemActive = (itemPath: string) =>
    path === itemPath || (itemPath !== '/mrp/dashboard' && path.startsWith(itemPath + '/'));

  // ── Sidebar content ─────────────────────────────────────────────────────────
  const sidebar = (
    <Box sx={{
      width: sidebarOpen ? W_OPEN : W_CLOSED,
      height: '100vh',
      bgcolor: C.bg,
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${C.border}`,
      overflow: 'hidden',
      transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      flexShrink: 0,
    }}>

      {/* ── Wordmark / logo ── */}
      <Box sx={{
        height: 52, minHeight: 52,
        display: 'flex', alignItems: 'center',
        justifyContent: sidebarOpen ? 'space-between' : 'center',
        px: sidebarOpen ? 2 : 0,
        borderBottom: `1px solid ${C.border}`,
        flexShrink: 0,
      }}>
        {sidebarOpen && (
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', userSelect: 'none' }}
          >
            {/* Hex logo mark */}
            <Box sx={{
              width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
              background: 'linear-gradient(135deg, #38BDF8 0%, #A78BFA 55%, #E879F9 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 14px #38BDF840, 0 0 6px #A78BFA30',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 13, letterSpacing: -0.5, lineHeight: 1 }}>
                M
              </Typography>
            </Box>
            <Box>
              <Typography sx={{
                fontWeight: 800, fontSize: 15, lineHeight: 1.1, letterSpacing: -0.4,
                background: 'linear-gradient(90deg, #E2E8F0 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Myelōs
              </Typography>
              <Typography sx={{ fontSize: 9.5, color: '#334155', letterSpacing: 0.5, lineHeight: 1.2 }}>
                Shannon Technologies
              </Typography>
            </Box>
          </Box>
        )}

        {!sidebarOpen && (
          <Tooltip title="Platform home" placement="right">
            <Box
              onClick={() => navigate('/')}
              sx={{
                width: 30, height: 30, borderRadius: '8px',
                background: 'linear-gradient(135deg, #38BDF8 0%, #A78BFA 55%, #E879F9 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 0 10px #38BDF840',
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 13, letterSpacing: -0.5, lineHeight: 1 }}>
                M
              </Typography>
            </Box>
          </Tooltip>
        )}

        {sidebarOpen && (
          <IconButton
            size="small"
            onClick={() => setSidebarOpen(false)}
            sx={{ color: C.dim, '&:hover': { color: C.text, bgcolor: C.hover }, p: 0.5, borderRadius: 1 }}
          >
            <ChevronLeft sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* ── Collapse toggle when closed ── */}
      {!sidebarOpen && (
        <Tooltip title="Expand navigation" placement="right">
          <Box
            onClick={() => setSidebarOpen(true)}
            sx={{
              height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.dim, cursor: 'pointer', borderBottom: `1px solid ${C.border}`,
              '&:hover': { color: C.mrp, bgcolor: C.hover },
            }}
          >
            <ChevronRight sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      )}

      {/* ── Scrollable nav body ── */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 2 },
      }}>

        {TOP_GROUPS.map(group => {
          if (!canSee(group.id)) return null;

          const isGroupOpen = group.isMrp ? mrpOpen : (openGroups[group.id] ?? false);
          const groupHasActive = group.isMrp
            ? onMrp
            : group.items?.some(i => isItemActive(i.path)) ?? false;

          return (
            <Box key={group.id} sx={{ mb: 0.25 }}>
              {/* ── Group header button ── */}
              <Tooltip title={!sidebarOpen ? group.label : ''} placement="right">
                <Box
                  onClick={() => {
                    if (!sidebarOpen) {
                      setSidebarOpen(true);
                      if (group.isMrp) { setMrpOpen(true); navigate('/mrp/dashboard'); }
                      else setOpenGroups(p => ({ ...p, [group.id]: true }));
                      return;
                    }
                    if (group.isMrp) {
                      if (!mrpOpen) navigate('/mrp/dashboard');
                      setMrpOpen(o => !o);
                    } else {
                      setOpenGroups(p => ({ ...p, [group.id]: !p[group.id] }));
                    }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    gap: sidebarOpen ? 1.25 : 0,
                    px: sidebarOpen ? 1.5 : 0,
                    py: 0.85,
                    mx: 0.75,
                    borderRadius: 1.25,
                    cursor: 'pointer',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    bgcolor: groupHasActive ? `${group.accent}12` : 'transparent',
                    border: `1px solid ${groupHasActive ? `${group.accent}25` : 'transparent'}`,
                    transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: `${group.accent}10`,
                      border: `1px solid ${group.accent}20`,
                    },
                  }}
                >
                  {/* Icon with accent glow when active */}
                  <Box sx={{
                    color: groupHasActive ? group.accent : C.muted,
                    display: 'flex', flexShrink: 0,
                    filter: groupHasActive ? `drop-shadow(0 0 4px ${group.accent}80)` : 'none',
                    transition: 'all 0.15s',
                  }}>
                    {group.icon}
                  </Box>

                  {sidebarOpen && (
                    <>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{
                          fontSize: 12.5, fontWeight: groupHasActive ? 700 : 500, lineHeight: 1.2,
                          color: groupHasActive ? group.accent : C.text,
                        }}>
                          {group.label}
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, color: C.muted, lineHeight: 1.2, letterSpacing: 0.2 }}>
                          {group.subtitle}
                        </Typography>
                      </Box>
                      <Box sx={{ color: C.dim, display: 'flex', flexShrink: 0 }}>
                        {isGroupOpen
                          ? <ExpandLess sx={{ fontSize: 14 }} />
                          : <ExpandMore sx={{ fontSize: 14 }} />}
                      </Box>
                    </>
                  )}
                </Box>
              </Tooltip>

              {/* ── MRP nested sub-groups ── */}
              {group.isMrp && sidebarOpen && (
                <Collapse in={mrpOpen} unmountOnExit>
                  <Box sx={{ ml: 1.5, mt: 0.25 }}>
                    {/* Dashboard link */}
                    <Box
                      onClick={() => navigate('/mrp/dashboard')}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.55,
                        borderRadius: 1, cursor: 'pointer', mb: 0.5,
                        bgcolor: path === '/mrp/dashboard' ? `${C.mrp}15` : 'transparent',
                        borderLeft: `2px solid ${path === '/mrp/dashboard' ? C.mrp : 'transparent'}`,
                        color: path === '/mrp/dashboard' ? C.mrp : C.muted,
                        '&:hover': { bgcolor: `${C.mrp}10`, color: C.text },
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 13 }} />
                      <Typography fontSize={12} fontWeight={path === '/mrp/dashboard' ? 600 : 400} color="inherit" noWrap>
                        Dashboard
                      </Typography>
                    </Box>

                    {MRP_GROUPS.map(sg => {
                      const sgActive = sg.items.some(i => isItemActive(i.path));
                      const sgOpen = mrpSubOpen[sg.id];
                      return (
                        <Box key={sg.id} sx={{ mb: 0.25 }}>
                          <Box
                            onClick={() => setMrpSubOpen(p => ({ ...p, [sg.id]: !p[sg.id] }))}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5,
                              borderRadius: 1, cursor: 'pointer',
                              color: sgActive ? sg.accent : C.muted,
                              '&:hover': { bgcolor: `${sg.accent}08`, color: sg.accent },
                            }}
                          >
                            <Box sx={{ color: 'inherit', display: 'flex', flexShrink: 0 }}>{sg.icon}</Box>
                            <Typography noWrap sx={{ flex: 1, fontSize: 10.5, fontWeight: 700, color: 'inherit',
                              textTransform: 'uppercase', letterSpacing: 0.8 }}>
                              {sg.label}
                            </Typography>
                            <Box sx={{ color: C.dim, display: 'flex' }}>
                              {sgOpen ? <ExpandLess sx={{ fontSize: 12 }} /> : <ExpandMore sx={{ fontSize: 12 }} />}
                            </Box>
                          </Box>

                          <Collapse in={sgOpen} unmountOnExit>
                            <Box sx={{ borderLeft: `1px solid ${sg.accent}30`, ml: 1.25, pl: 0.5 }}>
                              {sg.items.map(item => {
                                const active = isItemActive(item.path);
                                return (
                                  <Box
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    sx={{
                                      display: 'flex', alignItems: 'center', gap: 0.875,
                                      px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                                      bgcolor: active ? `${sg.accent}15` : 'transparent',
                                      borderLeft: `2px solid ${active ? sg.accent : 'transparent'}`,
                                      ml: -0.25,
                                      color: active ? sg.accent : C.muted,
                                      '&:hover': { bgcolor: `${sg.accent}10`, color: C.text },
                                      transition: 'all 0.1s',
                                    }}
                                  >
                                    <Box sx={{ color: 'inherit', display: 'flex', flexShrink: 0 }}>{item.icon}</Box>
                                    <Typography fontSize={11.5} fontWeight={active ? 600 : 400} color="inherit" noWrap>
                                      {item.label}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}

                    {/* Predictive Maintenance under MRP */}
                    {[
                      { label: 'Predictive Maintenance', path: '/predictive-maintenance', icon: <MaintenanceIcon sx={{ fontSize: 13 }} />, accent: '#F87171' },
                    ].map(item => (
                      <Box
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.55,
                          borderRadius: 1, cursor: 'pointer', mt: 0.5,
                          bgcolor: isItemActive(item.path) ? `${item.accent}15` : 'transparent',
                          borderLeft: `2px solid ${isItemActive(item.path) ? item.accent : 'transparent'}`,
                          color: isItemActive(item.path) ? item.accent : C.muted,
                          '&:hover': { bgcolor: `${item.accent}10`, color: C.text },
                        }}
                      >
                        {item.icon}
                        <Typography fontSize={12} fontWeight={isItemActive(item.path) ? 600 : 400} color="inherit" noWrap>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              )}

              {/* ── Regular group items ── */}
              {!group.isMrp && sidebarOpen && (
                <Collapse in={isGroupOpen} unmountOnExit>
                  <Box sx={{ ml: 1.5, mt: 0.25, borderLeft: `1px solid ${group.accent}25`, pl: 0.5 }}>
                    {group.items?.map(item => {
                      const active = isItemActive(item.path);
                      return (
                        <Box
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.6,
                            borderRadius: 1, cursor: 'pointer',
                            bgcolor: active ? `${group.accent}15` : 'transparent',
                            borderLeft: `2px solid ${active ? group.accent : 'transparent'}`,
                            ml: -0.25,
                            color: active ? group.accent : C.muted,
                            '&:hover': { bgcolor: `${group.accent}10`, color: C.text },
                            transition: 'all 0.1s',
                          }}
                        >
                          <Box sx={{ color: 'inherit', flexShrink: 0 }}>{item.icon}</Box>
                          <Typography fontSize={12} fontWeight={active ? 600 : 400} color="inherit" noWrap>
                            {item.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              )}

              {/* Divider after MRP and before WIP */}
              {(group.id === 'construction' || group.id === 'mrp') && (
                <Box sx={{ my: 0.75, mx: 1.5, borderBottom: `1px solid ${C.border}` }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Footer: user info + Neuron badge ── */}
      <Box sx={{
        borderTop: `1px solid ${C.border}`,
        flexShrink: 0,
        bgcolor: C.surface,
      }}>
        {/* Neuron AI status */}
        {sidebarOpen && (
          <Box sx={{ px: 1.75, py: 0.875, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ADE80', flexShrink: 0,
              boxShadow: '0 0 6px #4ADE8080',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }} />
            <Typography sx={{ fontSize: 9.5, color: '#334155', letterSpacing: 0.3 }}>
              Neuron AI · Active
            </Typography>
          </Box>
        )}

        {/* User row — display only; account menu lives in the top AppBar */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center',
            gap: sidebarOpen ? 1 : 0,
            px: sidebarOpen ? 1.5 : 0,
            py: 1,
            justifyContent: sidebarOpen ? 'flex-start' : 'center',
          }}
        >
          <Avatar
            sx={{
              width: 28, height: 28, fontSize: 12, fontWeight: 700,
              bgcolor: '#1E293B', color: C.muted, flexShrink: 0,
              border: `1px solid ${C.border}`,
            }}
          >
            {user?.username?.[0]?.toUpperCase() ?? 'U'}
          </Avatar>
          {sidebarOpen && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text, lineHeight: 1.2 }} noWrap>
                {user?.username}
              </Typography>
              <Typography sx={{ fontSize: 10, color: C.muted, lineHeight: 1.2, textTransform: 'capitalize' }}>
                {user?.role?.replace('_', ' ')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  // ── AppBar ──────────────────────────────────────────────────────────────────
  const appBarBg = onMrp ? '#06090F' : undefined;
  const appBarBorder = onMrp ? `1px solid ${C.border}` : '1px solid rgba(255,255,255,0.08)';

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />

      {/* ── Fixed top bar ── */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        height: 48, minHeight: 48,
        bgcolor: appBarBg,
        borderBottom: appBarBorder,
        display: 'flex', alignItems: 'center',
        pl: `${sidebarOpen ? W_OPEN : W_CLOSED}px`,
        pr: 2,
        transition: 'padding-left 0.22s cubic-bezier(0.4,0,0.2,1)',
        backdropFilter: 'blur(8px)',
        // Dark bg for all dark-page routes
        ...(onDarkPage && { bgcolor: '#06090F', borderBottom: `1px solid ${C.border}` }),
        ...(!onDarkPage && { bgcolor: undefined }),
      }}
        component="header"
        className={onDarkPage ? '' : 'MuiAppBar-root MuiAppBar-colorPrimary'}
      >
        {onDarkPage ? (
          // ── Dark-page branding (MRP + Neuron Studio) ──
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '7px', flexShrink: 0,
              background: 'linear-gradient(135deg, #38BDF8 0%, #A78BFA 55%, #E879F9 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 10px #38BDF840',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 11, lineHeight: 1 }}>M</Typography>
            </Box>
            <Box>
              <Typography sx={{
                fontWeight: 700, fontSize: 13.5, lineHeight: 1.1, letterSpacing: -0.3,
                background: 'linear-gradient(90deg, #E2E8F0 20%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {onMrp ? 'Myelōs MRP Platform' : 'Myelōs Platform'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#E879F9',
                  boxShadow: '0 0 4px #E879F9', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 9.5, color: '#475569', letterSpacing: 0.3 }}>
                  Powered by Neuron AI · Shannon Technologies
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          // ── Light-theme global branding ──
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, letterSpacing: -0.2 }}>
              Myelōs Platform
            </Typography>
            <Typography sx={{ fontSize: 10, opacity: 0.5, letterSpacing: 0.3, mt: 0.25 }}>
              Shannon Technologies
            </Typography>
          </Box>
        )}

        {/* Right actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ThemeToggle />

          {user && (
            <>
              {/* Role badge */}
              <Chip
                label={user.role?.replace('_', ' ')}
                size="small"
                variant="outlined"
                sx={{
                  height: 20, fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
                  color: onMrp ? '#64748B' : 'rgba(255,255,255,0.7)',
                  borderColor: onMrp ? C.border : 'rgba(255,255,255,0.2)',
                  textTransform: 'capitalize', display: { xs: 'none', sm: 'flex' },
                }}
              />

              {/* Vertical divider */}
              <Box sx={{ width: 1, height: 20, bgcolor: onMrp ? C.border : 'rgba(255,255,255,0.15)', mx: 0.25 }} />

              {/* User avatar button → opens dropdown */}
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1, py: 0.4, borderRadius: 1.25, cursor: 'pointer',
                  border: `1px solid ${onMrp ? C.border : 'rgba(255,255,255,0.12)'}`,
                  bgcolor: onMrp ? C.surface : 'rgba(255,255,255,0.05)',
                  '&:hover': {
                    bgcolor: onMrp ? C.hover : 'rgba(255,255,255,0.1)',
                    borderColor: onMrp ? '#334155' : 'rgba(255,255,255,0.25)',
                  },
                  transition: 'all 0.12s',
                }}
              >
                <Avatar
                  sx={{
                    width: 22, height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: onMrp ? '#1E293B' : 'rgba(255,255,255,0.15)',
                    color: onMrp ? '#94A3B8' : 'white',
                  }}
                >
                  {user.username?.[0]?.toUpperCase()}
                </Avatar>
                <Typography sx={{
                  fontSize: 12, fontWeight: 600,
                  color: onMrp ? '#94A3B8' : 'white',
                  display: { xs: 'none', sm: 'block' },
                }}>
                  {user.username}
                </Typography>
                <ExpandMore sx={{ fontSize: 13, color: onMrp ? '#475569' : 'rgba(255,255,255,0.5)' }} />
              </Box>

              {/* Account dropdown */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    bgcolor: '#0F172A', border: `1px solid ${C.border}`, color: C.text,
                    minWidth: 200, mt: 0.75,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  },
                }}
              >
                {/* User info header */}
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>{user.username}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.muted, textTransform: 'capitalize' }}>
                    {user.role?.replace('_', ' ')} · Shannon Technologies
                  </Typography>
                </Box>
                <MenuItem
                  onClick={() => setAnchorEl(null)}
                  sx={{ fontSize: 13, color: C.muted, gap: 1.25, py: 1,
                    '&:hover': { bgcolor: C.hover, color: C.text } }}
                >
                  <AccountIcon sx={{ fontSize: 16 }} /> Profile
                </MenuItem>
                <Divider sx={{ borderColor: C.border }} />
                <MenuItem
                  onClick={() => { logout(); setAnchorEl(null); navigate('/login'); }}
                  sx={{ fontSize: 13, color: '#F87171', gap: 1.25, py: 1,
                    '&:hover': { bgcolor: '#F8717115', color: '#F87171' } }}
                >
                  <LogoutIcon sx={{ fontSize: 16 }} /> Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* ── Sidebar ── */}
      {/* Desktop permanent */}
      <Box sx={{
        width: sidebarOpen ? W_OPEN : W_CLOSED,
        flexShrink: 0,
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
        mt: '48px',
      }}>
        {sidebar}
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          mt: '48px',
          overflow: 'auto',
          minWidth: 0,
          p: onDarkPage ? 0 : 3,
          bgcolor: onDarkPage ? '#0D1117' : undefined,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 3 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
